import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../../features/auth/providers/AuthProvider'
import { useApprovalOverview } from '../../features/approvals/hooks/useApprovalOverview'
import { getOnboardingResumePath } from '../../features/onboarding/lib/progress'
import { useOrganization } from '../../features/organizations/providers/OrganizationProvider'
import { MarloLogo } from '../../components/MarloLogo'

type NavItem = {
  to: string
  label: string
  icon: string
  showBadge?: boolean
  end?: boolean
}

type NavSection = {
  title: string
  items: NavItem[]
}

const onboardingActivePathGroups: Record<string, string[]> = {
  '/app/onboarding': [
    '/app/onboarding',
    '/app/onboarding/start',
    '/app/onboarding/history',
    '/app/onboarding/company',
    '/app/onboarding/vision',
    '/app/onboarding/goals',
    '/app/onboarding/strategy',
    '/app/onboarding/strategy-proof',
    '/app/onboarding/audience',
    '/app/onboarding/tone',
    '/app/onboarding/market',
    '/app/onboarding/review',
    '/app/onboarding/channels',
    '/app/onboarding/approval',
    '/app/onboarding/newsletter',
    '/app/onboarding/design',
  ],
}

function pathMatchesPrefix(pathname: string, prefix: string) {
  if (prefix === '/app/onboarding') {
    return pathname === '/app/onboarding'
  }

  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

function isSidebarItemActive(item: NavItem, pathname: string, defaultIsActive: boolean) {
  const onboardingGroup = onboardingActivePathGroups[item.to]
  if (onboardingGroup) {
    return onboardingGroup.some((prefix) => pathMatchesPrefix(pathname, prefix))
  }

  return defaultIsActive
}

const navSections: NavSection[] = [
  {
    title: 'Was ansteht',
    items: [
      { to: '/app/dashboard', label: 'Dashboard', icon: '▦' },
      { to: '/app/approvals', label: 'Zur Freigabe', icon: '✓', showBadge: true, end: true },
    ],
  },
  {
    title: 'Was Marlo macht',
    items: [
      { to: '/app/content', label: 'Beitraege & Plan', icon: '☷', end: true },
      { to: '/app/newsletter', label: 'Newsletter', icon: '✉', end: true },
      { to: '/app/knowledge-base', label: 'Wissensbasis', icon: '⊟', end: true },
    ],
  },
  {
    title: 'Hintergrund',
    items: [
      { to: '/app/onboarding', label: 'Meine Marke', icon: '✦' },
      { to: '/app/onboarding/review', label: 'Mein Paket', icon: '◈', end: true },
      { to: '/app/content', label: 'Was Marlo kann', icon: '⊞', end: true },
    ],
  },
]

function getInitials(fullName: string | null | undefined, email: string | null | undefined) {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase()
    }
  }

  if (email) {
    return email.slice(0, 2).toUpperCase()
  }

  return 'M'
}

export function AppShell() {
  const location = useLocation()
  const { signOut, user } = useAuth()
  const { organization } = useOrganization()
  const [isOnboardingSidebarOpen, setIsOnboardingSidebarOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const { data: approvalOverview } = useApprovalOverview()
  const pendingCount = approvalOverview?.pendingCount ?? 0
  const userLabel = user?.fullName ?? user?.email ?? 'Teammitglied'
  const companyLabel = organization?.name ?? 'Unternehmen'
  const initials = getInitials(user?.fullName, user?.email)
  const isOnboardingPath = location.pathname.startsWith('/app/onboarding')
  const isOnboardingWizardPath =
    isOnboardingPath &&
    location.pathname !== '/app/onboarding/start' &&
    location.pathname !== '/app/onboarding' &&
    location.pathname !== '/app/onboarding/history'
  const isOnboardingHistoryPath = location.pathname === '/app/onboarding/history'
  const onboardingResumePath = getOnboardingResumePath(organization?.onboardingMode, {
    completedStepKeys: organization?.onboardingCompletedSteps,
    completedAt: organization?.onboardingCompletedAt,
  }) ?? '/app/onboarding/company'

  useEffect(() => {
    if (location.pathname.startsWith('/app/onboarding')) {
      globalThis.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [location.pathname])

  useEffect(() => {
    if (!isOnboardingPath) {
      setIsOnboardingSidebarOpen(false)
    }
  }, [isOnboardingPath])

  const sidebarIsCollapsed = isOnboardingPath ? !isOnboardingSidebarOpen : isSidebarCollapsed

  const appClassName = [
    'app',
    sidebarIsCollapsed ? 'app-sidebar-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={appClassName}>
      <aside className={`sidebar ${sidebarIsCollapsed ? 'collapsed' : ''}`}>
          <button
            aria-label={sidebarIsCollapsed ? 'Sidebar aufklappen' : 'Sidebar einklappen'}
            className="sidebar-handle"
            onClick={() => {
              if (isOnboardingPath) {
                setIsOnboardingSidebarOpen((current) => !current)
                return
              }

              setIsSidebarCollapsed((current) => !current)
            }}
            title={sidebarIsCollapsed ? 'Sidebar aufklappen' : 'Sidebar einklappen'}
            type="button"
          >
            {sidebarIsCollapsed ? '›' : '‹'}
          </button>

        <MarloLogo size={40} />

        {navSections.map((section) => (
          <div className="nav-section" key={section.title}>
            <div className="nav-title">{section.title}</div>
            {section.items.map((item) => (
              <div key={item.to + item.label}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    isSidebarItemActive(item, location.pathname, isActive) ? 'nav-item active' : 'nav-item'
                  }
                >
                  <span className="icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.showBadge && pendingCount > 0 ? <span className="badge-count">{pendingCount}</span> : null}
                </NavLink>

                {section.title === 'Hintergrund' && item.label === 'Meine Marke' ? (
                  <div className="nav-item-children" aria-label="Meine Marke Unterpunkte">
                    <NavLink
                      to={onboardingResumePath}
                      className={isOnboardingWizardPath ? 'nav-subitem active' : 'nav-subitem'}
                    >
                      <span className="icon">•</span>
                      <span>Onboarding</span>
                    </NavLink>

                    <NavLink
                      to="/app/onboarding/history"
                      className={isOnboardingHistoryPath ? 'nav-subitem active nav-subitem-static' : 'nav-subitem nav-subitem-static'}
                    >
                      <span className="icon">↺</span>
                      <span>Historie</span>
                    </NavLink>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ))}

        <div className="sidebar-footer">
          <div className="avatar">{initials}</div>
          <div className="sidebar-user">
            <div className="user-name">{userLabel}</div>
            <div className="user-company">{companyLabel}</div>
          </div>
        </div>

        <button className="btn btn-ghost sidebar-signout" type="button" onClick={() => void signOut()}>
          {sidebarIsCollapsed ? '⎋' : 'Abmelden'}
        </button>
      </aside>

      <main className={isOnboardingPath ? 'main main-onboarding' : 'main'}>
        <Outlet />
      </main>
    </div>
  )
}